import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { AddRounded, ArrowBackRounded, EditRounded, KeyboardArrowRight } from '@mui/icons-material'
import { Button, Card, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { useAppDispatch } from '../../../store/Hooks'
import { changeRoute } from '../../../store/slices/SessionSlice'
import { isDuplicateKey } from './NewSchemaManager'
import { SchemaEditorAction, SchemaEditorState } from './schemaEditorReducer'
import SortableFieldItem from './SortableFieldItem'
import SortableStepItem from './SortableStepItem'

interface P {
    state: SchemaEditorState
    dispatch: React.Dispatch<SchemaEditorAction>
    isDefault: boolean
}

const NewSchemaEditor = ({ state, dispatch, isDefault }: P) => {
    const sensors = useSensors(useSensor(PointerSensor))
    const appDispatch = useAppDispatch()
    const activeStep = state.steps.find(
        s => s._id === state.activeStepId
    )

    if (state.viewMode === 'EDIT_STEP') {
        if (!activeStep) return null

        return (
            <Card sx={{ p: 3, height: '100%' }} elevation={2}>
                <Stack spacing={3} sx={{ p: 2 }} height='100%'>
                    <Stack spacing={2} justifyContent='space-between' alignItems='center' direction='row'>
                        <Stack direction='row' alignItems='center' spacing={2} sx={{ flex: 1 }}>
                            <Tooltip title='Back to Steps' arrow>
                                <IconButton
                                    onClick={() => dispatch({ type: 'BACK_TO_LIST' })}>
                                    <ArrowBackRounded />
                                </IconButton>
                            </Tooltip>
                            <TextField
                                disabled={isDefault}
                                fullWidth
                                label='Step Label'
                                value={activeStep.label}
                                onChange={e =>
                                    dispatch({
                                        type: 'UPDATE_STEP_LABEL',
                                        id: activeStep._id,
                                        label: e.target.value
                                    })}
                            />
                        </Stack>

                        <Button
                            disabled={isDefault}
                            variant='contained'
                            startIcon={<AddRounded />}
                            onClick={() =>
                                dispatch({
                                    type: 'ADD_FIELD',
                                    stepId: activeStep._id
                                })}>Add Field
                        </Button>
                    </Stack>

                    {/* FIELD LIST */}
                    <Stack spacing={1} overflow='auto'>

                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={event => {
                                const { active, over } = event
                                if (!over || active.id === over.id) return

                                const fields = [...activeStep.fields]

                                const oldIndex = fields.findIndex(field => field._id === active.id)
                                const newIndex = fields.findIndex(field => field._id === over.id)

                                const activeField = fields[oldIndex]

                                if (activeField.isDefault) return

                                const defaultCount = fields.filter(f => f.isDefault).length
                                const safeNewIndex = Math.max(newIndex, defaultCount)

                                const reordered = arrayMove(fields, oldIndex, safeNewIndex)

                                dispatch({
                                    type: 'REORDER_FIELDS',
                                    stepId: activeStep._id,
                                    fields: reordered
                                })
                            }}>
                            <SortableContext
                                items={activeStep.fields.map(f => f._id)}
                                strategy={verticalListSortingStrategy}>
                                <Stack
                                    spacing={1}
                                    sx={{
                                        flex: 1,
                                        minHeight: '100%',
                                        pb: 4
                                    }}>
                                    {activeStep.fields.map(field => {
                                        return (
                                            <SortableFieldItem
                                                hasKeyError={isDuplicateKey(activeStep.fields, field.key, field._id)}
                                                state={state}
                                                dispatch={dispatch}
                                                key={field._id}
                                                field={field}

                                                onDelete={fieldId => dispatch({
                                                    type: 'REMOVE_FIELD',
                                                    stepId: activeStep._id,
                                                    fieldId
                                                })}

                                                onUpdateField={(fieldId, updates) => dispatch({
                                                    type: 'UPDATE_FIELD',
                                                    stepId: activeStep._id,
                                                    fieldId,
                                                    updates
                                                })}

                                            />

                                        )
                                    })}
                                </Stack>
                            </SortableContext>
                        </DndContext>

                    </Stack>

                </Stack>
            </Card>
        )
    }

    return (

        // Step List UI
        <Card sx={{ p: 3, height: '100%' }} elevation={2}>
            <Stack spacing={1} sx={{ p: 2, height: '100%' }}>

                <Stack justifyContent='space-between' alignItems='center' direction='row'>
                    <Stack direction='row' alignItems='center' spacing={2}>
                        <Tooltip title='Back to Schemas' arrow>
                            <IconButton
                                onClick={() => appDispatch(changeRoute({ route: 'contracts', params: { tab: 'schemas' } }))}>
                                <ArrowBackRounded />
                            </IconButton>
                        </Tooltip>
                        <Typography variant='h6'>Select Step to start editing</Typography>
                    </Stack>

                    <Tooltip arrow title={!state.activeStepId ? 'Select a Step to Edit/Open' : activeStep?.isDefault ? 'View Step' : 'Edit Step'}>
                        <span>
                            <Button
                                variant='contained'
                                disabled={!state.activeStepId}
                                startIcon={activeStep?.isDefault ? <KeyboardArrowRight /> : <EditRounded />}
                                onClick={() => dispatch({ type: 'OPEN_EDIT' })}> {activeStep?.isDefault ? 'View' : 'Edit'}
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>

                <Stack overflow='auto'>

                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={event => {
                            const { active, over } = event
                            if (!over || active.id === over.id) return

                            const steps = [...state.steps]

                            const oldIndex = steps.findIndex(step => step._id === active.id)
                            const newIndex = steps.findIndex(step => step._id === over.id)

                            const activeStep = steps[oldIndex]

                            if (activeStep.isDefault) return

                            const defaultCount = steps.filter(s => s.isDefault).length
                            const safeNewIndex = Math.max(newIndex, defaultCount)
                            const reordered = arrayMove(steps, oldIndex, safeNewIndex)

                            dispatch({
                                type: 'REORDER_STEPS',
                                steps: reordered
                            })
                        }}>
                        <SortableContext
                            items={state.steps.map(step => step._id)}
                            strategy={verticalListSortingStrategy}>
                            <Stack
                                spacing={1}
                                sx={{
                                // flex: 1,
                                    minHeight: '100%',
                                    pb: 4
                                }}>
                                {state.steps.map(step => (
                                    <SortableStepItem
                                        key={step._id}
                                        step={step}
                                        isSelected={state.activeStepId === step._id}
                                        onSelect={id => dispatch({ type: 'SELECT_STEP', id })}
                                        onDelete={id => dispatch({ type: 'REMOVE_STEP', id })}
                                    />
                                ))}
                            </Stack>
                        </SortableContext>
                    </DndContext>

                </Stack>

                <Stack alignItems='center'>
                    <Tooltip title={isDefault ? 'Cant edit a default schema' : 'Add Step'} arrow>
                        <span>
                            <Button
                                disabled={isDefault}
                                variant='contained'
                                color='primary'
                                onClick={() => dispatch({ type: 'ADD_STEP' })}>
                                <AddRounded />
                            </Button>
                        </span>
                    </Tooltip>

                </Stack>
            </Stack>
        </Card>
    )
}

export default NewSchemaEditor
